import { useMemo, useState } from 'react'
import AnimalImage from '../components/AnimalImage.jsx'
import { categoryProgress, getCollection, resetCollection } from '../lib/collection.js'
import { track } from '../lib/analytics.js'
import { widestWordEm } from '../lib/name_fit.js'

/**
 * SPEC screen 8: which animals the kid has used and how many battles each has
 * won, plus "Words I know" (SPEC Section 8). localStorage only, purely for fun.
 *
 * Polish (step 9): a champion card, and tapping an animal starts a fresh battle
 * with it already picked — the collection is the second way into a fight, not
 * a dead end (D-061). The progress is counted by category (D-134): "2 of 9 big
 * cats", every group of the roster listed, the untouched ones at zero, so the
 * roster reads as a set to finish. Counts only, never a percentage.
 */
export default function Collection({ roster, onBack, onRematch, onCredits }) {
  const [c, setC] = useState(getCollection)
  const [confirmReset, setConfirmReset] = useState(false)
  const bySlug = useMemo(() => new Map(roster.animals.map((x) => [x.slug, x])), [roster])

  const used = Object.entries(c.animals)
    .map(([slug, e]) => ({ ...e, animal: bySlug.get(slug) }))
    .filter((e) => e.animal)
    .sort((x, y) => y.wins - x.wins || y.battles - x.battles || x.animal.name.localeCompare(y.animal.name))
  const words = Object.values(c.words).sort((x, y) => y.at - x.at)
  const battles = Math.round(used.reduce((s, e) => s + e.battles, 0) / 2)
  const champ = used[0]
  const groups = categoryProgress(roster.groups, c)
  const surprises = Object.keys(c.surprises).length

  const doReset = () => {
    track('collection_reset')
    resetCollection()
    setC(getCollection())
    setConfirmReset(false)
  }

  return (
    <section className="screen collection">
      <div className="collection__head">
        <h1>🏆 My collection</h1>
        <button className="btn" onClick={onBack}>◀ Back to the animals</button>
      </div>

      {!used.length ? (
        <p className="collection__empty">No battles yet. Pick two animals and press Fight!</p>
      ) : (
        <>
          {champ && champ.wins > 0 && (
            <div className="champ">
              <AnimalImage slug={champ.animal.slug} pose="victorious" alt="" />
              <div>
                <small>Your champion</small>
                <strong>{champ.animal.name}</strong>
                <span>{champ.wins} win{champ.wins === 1 ? '' : 's'} from {champ.battles} battle{champ.battles === 1 ? '' : 's'}</span>
              </div>
            </div>
          )}

          <div className="progress">
            <span className="progress__total">
              {used.length} of {roster.animals.length} animals met · {battles} battle{battles === 1 ? '' : 's'}
              {/* The calls (D-135) and the surprises (D-136): counts that only go
                  up, never a share of the calls made or the battles played. */}
              {c.calls.made > 0 && <> · {c.calls.right} right call{c.calls.right === 1 ? '' : 's'}</>}
              {surprises > 0 && <> · {surprises} surprise{surprises === 1 ? '' : 's'}</>}
            </span>
            <ul className="progress__cats" aria-label="Animals met, by group">
              {groups.map((g) => (
                <li key={g.category} className={`progress__cat progress__cat--${g.category}${g.met ? '' : ' is-empty'}${g.met === g.total ? ' is-done' : ''}`}>
                  <small>{g.label}</small>
                  <b>{g.met} of {g.total}</b>
                  {/* The bar is the same count drawn as a length; the number beside it is the only figure. */}
                  <i className="progress__bar" aria-hidden="true"><i style={{ width: `${(100 * g.met) / g.total}%` }} /></i>
                </li>
              ))}
            </ul>
          </div>

          <h2>Animals I have battled <small>tap one to fight again</small></h2>
          <div className="collection__grid">
            {used.map((e) => (
              <button key={e.animal.slug} className="collection__card" style={{ '--name-em': widestWordEm(e.animal.name) }} onClick={() => onRematch(e.animal.slug)} aria-label={`Battle with ${e.animal.name}`}>
                <AnimalImage slug={e.animal.slug} pose={e.wins ? 'victorious' : 'neutral'} alt="" lazy />
                <b>{e.animal.name}</b>
                <small><span className="collection__wins">{e.wins} win{e.wins === 1 ? '' : 's'}</span> · {e.battles} battle{e.battles === 1 ? '' : 's'}</small>
              </button>
            ))}
          </div>
        </>
      )}

      <h2>📖 Words I know {words.length > 0 && <small>{words.length} collected</small>}</h2>
      {!words.length ? (
        <p className="collection__empty">Age 6 stories teach a new word each. They will collect here.</p>
      ) : (
        <div className="collection__words">
          {words.map((w) => <div key={w.word} className="collection__word"><strong>{w.word}</strong>{w.definition}</div>)}
        </div>
      )}

      {/* The photos are licensed and the licences ask for a credit; this is where
          it is given, off the pictures themselves (D-143). */}
      <p className="collection__credits">
        <button className="linkbtn" onClick={onCredits}>Photo credits</button>
      </p>

      {used.length > 0 && (
        <div className="collection__reset">
          {confirmReset ? (
            <>
              <span>Erase every battle and word?</span>
              <button className="btn btn--small" onClick={() => setConfirmReset(false)}>No, keep them</button>
              <button className="btn btn--small btn--danger" onClick={doReset}>Yes, start over</button>
            </>
          ) : (
            <button className="btn btn--small collection__resetbtn" onClick={() => setConfirmReset(true)}>Start over</button>
          )}
        </div>
      )}
    </section>
  )
}
